import { LocalityTypes } from '../../../Generated/LocalityType.js'
import { MatrixTypes } from '../../../Generated/MatrixType.js'
import { SequencingChemistrys } from '../../../Generated/SequencingChemistry.js'
import { Sequencers } from '../../../Generated/Sequencer.js'
import { SequencingPlatforms } from '../../../Generated/SequencingPlatform.js'
import { CollectionTypes } from '../../../Generated/CollectionType.js'
import { TrapTypes } from '../../../Generated/TrapType.js'
import { ExtractionKits } from '../../../Generated/ExtractionKit.js'
import { HabitatTypes } from '../../../Generated/HabitatType.js'
import type { VisualizationSampleContainer } from './VisualizationSampleContainer.js'
import { formatDate, parseDate } from '../../../Util/DateHandling.js'

/**
 * Without a `uiLabel` a grouping falls back to the raw stored value, and for these fields that is the
 * full ISO timestamp with its offset — so a chart axis read `2026-06-01T23:00:00+02:00 [3]`. Formatting
 * here changes the label only; the bucket key is untouched, so saved selections keep matching.
 */
const dateLabel = (value: string | undefined) => (value ? formatDate(parseDate(value)) : undefined)

export function keyByKeyedObject<U extends string, T extends { key: U } = { key: U }>(ops: Readonly<T[]>): Record<U, T> {
  const result: Record<U, T> = {} as Record<U, T>
  for (let e of ops) {
    result[e.key] = e
  }
  return result
}

export type MetadataType = 'string' | 'enum' | 'datetime' | 'datetime:duration'

export type MetadataFieldDescriptor<T extends string = string> = {
  type: MetadataType
  key: T
  label: string
  uiLabel?: (sample: VisualizationSampleContainer) => string | undefined
  getter: (sample: VisualizationSampleContainer) => unknown | undefined
  comparisonKey?: (sample: VisualizationSampleContainer) => string
}

export type MetadataFields<T extends string = string> = Record<T, MetadataFieldDescriptor<T>>

function createMetadataFieldDescriptors<T extends string>(m: MetadataFieldDescriptor<T>[]) {
  return m
}

const MetaDataFields = createMetadataFieldDescriptors([
  {
    key: 'fileNames',
    type: 'string',
    label: 'File Names',
    getter: (sample) => `${sample.forwardFile?.fileName} ${sample.reverseFile?.fileName}`
  },
  {
    key: 'forwardPrimer',
    type: 'string',
    label: 'Forward Primer',
    getter: (sample) => sample.forwardPrimer
  },
  {
    key: 'reversePrimer',
    type: 'string',
    label: 'Reverse Primer',
    getter: (sample) => sample.reversePrimer
  },
  {
    key: 'sampleMetadata.forwardPrimerRequested',
    type: 'string',
    label: 'Forward Primer (Customer)',
    getter: (sample) => sample.sampleMetadata?.forwardPrimerRequested
  },
  {
    key: 'sampleMetadata.reversePrimerRequested',
    type: 'string',
    label: 'Reverse Primer (Customer)',
    getter: (sample) => sample.sampleMetadata?.reversePrimerRequested
  },
  // ───── Basic Metadata ─────
  {
    key: 'sampleMetadata.samplingDate',
    type: 'datetime',
    label: 'Sampling Date',
    getter: (sample) => sample.sampleMetadata?.samplingDate,
    uiLabel: (sample) => dateLabel(sample.sampleMetadata?.samplingDate)
  },
  {
    key: 'sampleMetadata.samplingStart',
    type: 'datetime',
    label: 'Sampling Start',
    getter: (sample) => sample.sampleMetadata?.samplingStart,
    uiLabel: (sample) => dateLabel(sample.sampleMetadata?.samplingStart)
  },
  {
    key: 'sampleMetadata.samplingEnd',
    type: 'datetime',
    label: 'Sampling End',
    getter: (sample) => sample.sampleMetadata?.samplingEnd,
    uiLabel: (sample) => dateLabel(sample.sampleMetadata?.samplingEnd)
  },
  {
    key: 'sampleMetadata.habitatType',
    type: 'enum',
    label: 'Habitat Type',
    getter: (sample) => sample.sampleMetadata?.habitatType,
    uiLabel: (sample) => (sample.sampleMetadata?.habitatType ? HabitatTypes[sample.sampleMetadata.habitatType].label : undefined)
  },
  {
    key: 'sampleMetadata.localityType',
    type: 'enum',
    label: 'Locality Type',
    getter: (sample) => sample.sampleMetadata?.localityType,
    uiLabel: (sample) => (sample.sampleMetadata?.localityType ? LocalityTypes[sample.sampleMetadata.localityType].label : undefined)
  },
  {
    key: 'sampleMetadata.extractionKit',
    type: 'enum',
    label: 'Extraction Kit',
    getter: (sample) => sample.sampleMetadata?.extractionKit,
    uiLabel: (sample) => (sample.sampleMetadata?.extractionKit ? ExtractionKits[sample.sampleMetadata.extractionKit].label : undefined)
  },
  {
    key: 'sampleMetadata.matrixType',
    type: 'enum',
    label: 'Matrix Type',
    getter: (sample) => sample.sampleMetadata?.matrixType,
    uiLabel: (sample) => (sample.sampleMetadata?.matrixType ? MatrixTypes[sample.sampleMetadata.matrixType].label : undefined)
  },
  {
    key: 'sampleMetadata.trapType',
    type: 'enum',
    label: 'Trap Type',
    getter: (sample) => sample.sampleMetadata?.trapType,
    uiLabel: (sample) => (sample.sampleMetadata?.trapType ? TrapTypes[sample.sampleMetadata.trapType].label : undefined)
  },
  {
    key: 'sampleMetadata.collectionType',
    type: 'enum',
    label: 'Collection Type',
    getter: (sample) => sample.sampleMetadata?.collectionType,
    uiLabel: (sample) => (sample.sampleMetadata?.collectionType ? CollectionTypes[sample.sampleMetadata.collectionType].label : undefined)
  },
  {
    key: 'sampleMetadata.sequencer',
    type: 'enum',
    label: 'Sequencer',
    getter: (sample) => sample.sampleMetadata?.sequencer,
    uiLabel: (sample) => (sample.sampleMetadata?.sequencer ? Sequencers[sample.sampleMetadata.sequencer].label : undefined)
  },
  {
    key: 'sampleMetadata.sequencingChemistry',
    type: 'enum',
    label: 'Sequencing Chemistry',
    getter: (sample) => sample.sampleMetadata?.sequencingChemistry,
    uiLabel: (sample) =>
      sample.sampleMetadata?.sequencingChemistry ? SequencingChemistrys[sample.sampleMetadata.sequencingChemistry].label : undefined
  },
  {
    key: 'sampleMetadata.sequencingDepth',
    type: 'string',
    label: 'Sequencing Depth',
    getter: (sample) => sample.sampleMetadata?.sequencingDepth
  },

  // ───── Geo Fields ─────
  {
    key: 'sampleMetadata.geo.geoCountryName',
    type: 'string',
    label: 'Country',
    getter: (sample) => sample.sampleMetadata?.geo?.geoCountryName
  },
  {
    key: 'sampleMetadata.geo.geoCountryIsoA3',
    type: 'string',
    label: 'Country Code (ISO A3)',
    getter: (sample) => sample.sampleMetadata?.geo?.geoCountryIsoA3
  },
  {
    key: 'sampleMetadata.geo.geoStateName',
    type: 'string',
    label: 'State Name',
    getter: (sample) => sample.sampleMetadata?.geo?.geoStateName
  },
  {
    key: 'sampleMetadata.geo.geoContinent',
    type: 'string',
    label: 'Continent',
    getter: (sample) => sample.sampleMetadata?.geo?.geoContinent
  },
  {
    key: 'sampleMetadata.geo.geoRegionUN',
    type: 'string',
    label: 'UN Region',
    getter: (sample) => sample.sampleMetadata?.geo?.geoRegionUN
  },
  {
    key: 'sampleMetadata.geo.geoRegionWB',
    type: 'string',
    label: 'World Bank Region',
    getter: (sample) => sample.sampleMetadata?.geo?.geoRegionWB
  },
  {
    key: 'sampleMetadata.geo.geoSubregion',
    type: 'string',
    label: 'Subregion',
    getter: (sample) => sample.sampleMetadata?.geo?.geoSubregion
  },
  {
    key: 'sampleMetadata.samplingDuration',
    type: 'datetime:duration',
    label: 'Sampling duration',
    getter: (sample) =>
      sample.sampleMetadata?.samplingStart && sample.sampleMetadata?.samplingEnd
        ? [sample.sampleMetadata?.samplingStart, sample.sampleMetadata?.samplingEnd]
        : undefined,
    comparisonKey: (sample) => `${sample.sampleMetadata?.samplingStart}--${sample.sampleMetadata?.samplingEnd}`,
    uiLabel: (sample) => {
      const from = dateLabel(sample.sampleMetadata?.samplingStart)
      const to = dateLabel(sample.sampleMetadata?.samplingEnd)
      return from && to ? `${from} – ${to}` : undefined
    }
  },
  {
    key: 'sampleMetadata.customerSampleName',
    type: 'string',
    label: 'Sample Name',
    getter: (sample) => sample.sampleMetadata?.customerSampleName
  },
  {
    /*
     * Sibling of `sampleMetadata.sequencer`, and deliberately separate from it: that one is what the
     * customer declared, this one is what the reads say. They can disagree, and the disagreement is
     * worth seeing.
     */
    key: 'detectedSequencingPlatform',
    type: 'enum',
    label: 'Sequencing Platform',
    getter: (sample) => sample.detectedSequencingPlatform,
    uiLabel: (sample) =>
      sample.detectedSequencingPlatform ? SequencingPlatforms[sample.detectedSequencingPlatform].label : undefined
  },
  {
    key: 'poolLabel',
    type: 'string',
    label: 'Pool',
    getter: (sample) => sample.poolLabel
  },
  {
    key: 'orderId',
    type: 'string',
    uiLabel: (sample) => {
      console.info('looking at sample', sample)
      return sample?.order?.name ?? sample?.orderId
    },
    label: 'Order ID',
    getter: (sample) => sample.orderId
  }
])

export type Metadatafieldtypes = (typeof MetaDataFields)[number]['key']
export const STATIC_METADATA_FIELDS = keyByKeyedObject<Metadatafieldtypes, MetadataFieldDescriptor<Metadatafieldtypes>>(MetaDataFields)

export function generateMetadataFields(samples: VisualizationSampleContainer[]): MetadataFields {
  const customKeys = new Set<string>(
    samples.flatMap((sample) => (sample.sampleMetadata?.customAttributes ? Object.keys(sample.sampleMetadata.customAttributes) : []))
  )

  // Create a MetadataFieldDescriptor for each custom key
  const customFields: MetadataFieldDescriptor[] = Array.from(customKeys).map((key) => ({
    key: `sampleMetadata.customAttributes.${key}`,
    type: 'string', // Assuming all custom attributes are strings
    label: `Custom: ${key}`,
    getter: (sample) => sample.sampleMetadata?.customAttributes?.[key]
  }))

  return {
    ...STATIC_METADATA_FIELDS,
    ...keyByKeyedObject(customFields)
  }
}

export function isExistingMetadataField(value: string, fields: MetadataFields): boolean {
  return value in fields
}
